import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { peticion } from '../api/cliente.js';
import type { Entidad } from '../api/tipos.js';
import { Aviso, Cargando, EstadoVacio } from '../componentes/comunes.js';
import { LOGOS } from '../logos.js';
import { textos } from '../textos.js';

/**
 * Paso 1: elegir entidad.
 *
 * Va **antes** del acceso porque determina contra qué maestro se comprueban los
 * datos (§3, §9). No exige sesión.
 */
export function PantallaEntidad({ alElegir }: { alElegir: (entidad: Entidad) => void }) {
  const consulta = useQuery({
    queryKey: ['entidades'],
    queryFn: () => peticion<{ entidades: Entidad[] }>('/entidades'),
  });
  const entidades = consulta.data?.entidades ?? [];

  return (
    <section className="pantalla-entidad" aria-labelledby="titulo-entidad">
      <h2 id="titulo-entidad">{textos.entidad.titulo}</h2>
      <p className="ayuda">{textos.entidad.ayuda}</p>

      {consulta.isPending && <Cargando />}
      {consulta.isError && (
        <Aviso tipo="error">
          {textos.errores.generico}{' '}
          <button type="button" className="enlace" onClick={() => void consulta.refetch()}>
            {textos.aplicacion.reintentar}
          </button>
        </Aviso>
      )}

      {consulta.data !== undefined && entidades.length === 0 && (
        <EstadoVacio titulo={textos.entidad.ninguna} detalle={textos.entidad.ningunaAyuda} />
      )}

      {entidades.length > 0 && (
        <ul className="opciones">
          {entidades.map((entidad) => (
            <li key={entidad.codigo}>
              <button type="button" className="opcion" onClick={() => alElegir(entidad)}>
                <ContenidoEntidad entidad={entidad} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Antepone «TS» al nombre de la marca, salvo que ya empiece por ahí (p. ej. «TS Facturas»). */
function conPrefijoTs(nombre: string): string {
  return nombre.startsWith('TS ') ? nombre : `TS ${nombre}`;
}

/**
 * Lo que va dentro de la tarjeta: el logo, que ya lleva el nombre, o el icono
 * de TeamSystem con el nombre escrito si no hay logo.
 */
function ContenidoEntidad({ entidad }: { entidad: Entidad }) {
  const [fallaElLogo, setFallaElLogo] = useState(false);
  const logo = LOGOS[entidad.codigo];
  const nombre = conPrefijoTs(entidad.nombre);

  if (logo !== undefined && !fallaElLogo) {
    return (
      <span className="logo-opcion">
        <img
          src={`/logos/${logo.fichero}`}
          alt={nombre}
          style={{ height: logo.alto }}
          onError={() => setFallaElLogo(true)}
        />
      </span>
    );
  }

  return (
    <>
      <span
        className="icono-opcion"
        style={{ color: entidad.colorHex ?? 'var(--acento)' }}
        aria-hidden="true"
      >
        <img src="/teamsystem-icon.svg" alt="" />
      </span>
      <span className="texto-opcion">
        <span className="nombre">{nombre}</span>
      </span>
    </>
  );
}
